import React, { useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Typography,
  alpha,
  Divider,
  Switch,
  MenuItem,
  TextField,
  Button,
  Chip,
} from '@mui/material';
import {
  TuneRounded,
  NotificationsRounded,
  SpeedRounded,
  PaletteRounded,
  SaveRounded,
  RestartAltRounded,
  SecurityRounded,
} from '@mui/icons-material';
import { palette } from '../theme';
import { GlassPanel, SectionTitle, StatusPill } from '../components/ui/GlassPanel';
import { useToast } from '../components/ui/ToastProvider';
import { useAuth } from '../App';

const DEFAULTS = {
  refreshInterval: 15,
  density: 'comfortable',
  notifications: true,
  soundAlerts: false,
  reduceMotion: false,
  holographicPanels: true,
  compactTables: false,
  defaultRange: '24H',
};

const STORAGE_KEY = 'bluenode.console.preferences';

export default function Settings() {
  const toast = useToast();
  const { user } = useAuth();
  const [preferences, setPreferences] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
      return { ...DEFAULTS, ...stored };
    } catch (error) {
      return DEFAULTS;
    }
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
  }, [preferences]);

  const update = (key, value) => setPreferences((current) => ({ ...current, [key]: value }));

  const panels = [
    {
      title: 'Live Data Refresh',
      subtitle: 'Polling cadence for dashboards and device telemetry',
      icon: <SpeedRounded sx={{ fontSize: 18 }} />,
      control: (
        <TextField
          select
          size="small"
          value={preferences.refreshInterval}
          onChange={(event) => update('refreshInterval', Number(event.target.value))}
          sx={{ minWidth: 150 }}
        >
          {[5, 10, 15, 30, 60].map((seconds) => (
            <MenuItem key={seconds} value={seconds}>
              {seconds} seconds
            </MenuItem>
          ))}
        </TextField>
      ),
    },
    {
      title: 'Interface Density',
      subtitle: 'Spacing scale applied across tables and panels',
      icon: <PaletteRounded sx={{ fontSize: 18 }} />,
      control: (
        <TextField
          select
          size="small"
          value={preferences.density}
          onChange={(event) => update('density', event.target.value)}
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="comfortable">Comfortable</MenuItem>
          <MenuItem value="compact">Compact</MenuItem>
        </TextField>
      ),
    },
    {
      title: 'Default Time Range',
      subtitle: 'Window applied when opening device data views',
      icon: <SpeedRounded sx={{ fontSize: 18 }} />,
      control: (
        <TextField
          select
          size="small"
          value={preferences.defaultRange}
          onChange={(event) => update('defaultRange', event.target.value)}
          sx={{ minWidth: 150 }}
        >
          {['1H', '6H', '24H', '7D', '30D'].map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </TextField>
      ),
    },
  ];

  const toggles = [
    {
      title: 'Real-time notifications',
      subtitle: 'Surface new alerts in the navigation badge',
      icon: <NotificationsRounded sx={{ fontSize: 18 }} />,
      key: 'notifications',
    },
    {
      title: 'Audible alert tone',
      subtitle: 'Play a short tone when a critical alert arrives',
      icon: <NotificationsRounded sx={{ fontSize: 18 }} />,
      key: 'soundAlerts',
    },
    {
      title: 'Holographic surfaces',
      subtitle: 'Enable glass panels, volumetric glow and 3D depth layers',
      icon: <PaletteRounded sx={{ fontSize: 18 }} />,
      key: 'holographicPanels',
    },
    {
      title: 'Reduce motion',
      subtitle: 'Minimise floating and parallax animations for focus',
      icon: <SpeedRounded sx={{ fontSize: 18 }} />,
      key: 'reduceMotion',
    },
    {
      title: 'Compact data tables',
      subtitle: 'Render table rows with reduced vertical padding',
      icon: <TuneRounded sx={{ fontSize: 18 }} />,
      key: 'compactTables',
    },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <GlassPanel sx={{ p: 3 }}>
        <SectionTitle
          title="Console Preferences"
          subtitle="Local display and interaction settings for this operator session"
          icon={<TuneRounded sx={{ fontSize: 18 }} />}
          action={
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<RestartAltRounded sx={{ fontSize: 15 }} />}
                onClick={() => {
                  setPreferences(DEFAULTS);
                  toast.info('Preferences reset to defaults.');
                }}
              >
                Reset
              </Button>
              <Button
                size="small"
                variant="contained"
                startIcon={<SaveRounded sx={{ fontSize: 15 }} />}
                onClick={() => toast.success('Preferences saved for this session.')}
              >
                Save
              </Button>
            </Box>
          }
        />
      </GlassPanel>

      <Grid container spacing={2.5}>
        <Grid item xs={12} lg={7}>
          <GlassPanel sx={{ p: 3, height: '100%' }}>
            <SectionTitle title="Display & Data" subtitle="How the console renders and refreshes data" icon={<SpeedRounded sx={{ fontSize: 18 }} />} />
            <Box sx={{ mt: 2.25, display: 'grid', gap: 1.5 }}>
              {panels.map((panel) => (
                <Box
                  key={panel.title}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    p: 1.75,
                    borderRadius: 3,
                    border: `1px solid ${alpha(palette.primary, 0.12)}`,
                    background: alpha(palette.primary, 0.04),
                  }}
                >
                  <Box sx={{ color: palette.primary, display: 'flex' }}>{panel.icon}</Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: '0.82rem', fontWeight: 600 }}>{panel.title}</Typography>
                    <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>{panel.subtitle}</Typography>
                  </Box>
                  {panel.control}
                </Box>
              ))}
            </Box>
          </GlassPanel>
        </Grid>

        <Grid item xs={12} lg={5}>
          <GlassPanel sx={{ p: 3, height: '100%' }}>
            <SectionTitle title="Behaviour" subtitle="Interaction and visual effects" icon={<PaletteRounded sx={{ fontSize: 18 }} />} />
            <Box sx={{ mt: 2.25, display: 'grid', gap: 1.25 }}>
              {toggles.map((toggle) => (
                <Box
                  key={toggle.key}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    p: 1.5,
                    borderRadius: 3,
                    border: `1px solid ${alpha(palette.primary, 0.1)}`,
                    background: alpha(palette.primary, 0.03),
                  }}
                >
                  <Box sx={{ color: 'text.secondary', display: 'flex' }}>{toggle.icon}</Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 600 }}>{toggle.title}</Typography>
                    <Typography sx={{ fontSize: '0.69rem', color: 'text.secondary', lineHeight: 1.4 }}>
                      {toggle.subtitle}
                    </Typography>
                  </Box>
                  <Switch checked={preferences[toggle.key]} onChange={(event) => update(toggle.key, event.target.checked)} />
                </Box>
              ))}
            </Box>
          </GlassPanel>
        </Grid>
      </Grid>

      <GlassPanel sx={{ p: 3 }}>
        <SectionTitle
          title="Security Posture"
          subtitle="Enforced platform controls for this session"
          icon={<SecurityRounded sx={{ fontSize: 18 }} />}
        />
        <Box sx={{ mt: 2.25, display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' } }}>
          {[
            { label: 'Authentication', value: 'Required' },
            { label: 'Password storage', value: 'Hashed' },
            { label: 'Role based access', value: 'Enforced' },
            { label: 'Session lifetime', value: '30 minutes' },
          ].map((item) => (
            <Box
              key={item.label}
              sx={{
                p: 1.75,
                borderRadius: 3,
                border: `1px solid ${alpha(palette.success, 0.16)}`,
                background: alpha(palette.success, 0.05),
              }}
            >
              <Typography sx={{ fontSize: '0.62rem', letterSpacing: '0.14em', color: 'text.secondary' }}>
                {item.label.toUpperCase()}
              </Typography>
              <Box sx={{ mt: 0.75 }}>
                <StatusPill label={item.value} tone="success" pulse={false} />
              </Box>
            </Box>
          ))}
        </Box>
        <Divider sx={{ borderColor: alpha(palette.primary, 0.1), my: 2.5 }} />
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip size="small" label={`Operator: ${user?.username}`} sx={{ fontSize: '0.68rem', background: alpha('#334155', 0.28) }} />
          <Chip
            size="small"
            label={`Role: ${user?.role}`}
            sx={{ fontSize: '0.68rem', background: alpha(palette.secondary, 0.14), color: palette.secondary }}
          />
          <Chip size="small" label="Audit logging active" sx={{ fontSize: '0.68rem', background: alpha(palette.success, 0.14), color: palette.success }} />
        </Box>
      </GlassPanel>
    </Box>
  );
}
